import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePurchaseAddon, formatAddonAmount } from '@/hooks/addons';
import { useState } from 'react';
import { Coins, HardDrive, Infinity, ShoppingCart, Minus, Plus } from 'lucide-react';
import type { AddonCatalog } from '@shared/schema';

interface AddonCatalogCardProps {
  addon: AddonCatalog & { price: number };
  className?: string;
  'data-testid'?: string;
}

export function AddonCatalogCard({ 
  addon, 
  className = '',
  'data-testid': testId 
}: AddonCatalogCardProps) {
  const [quantity, setQuantity] = useState(1);
  const purchaseAddon = usePurchaseAddon();

  const handlePurchase = () => {
    purchaseAddon.mutate({
      items: [{ sku: addon.sku, quantity }]
    });
  };

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(Math.max(1, Math.min(10, newQuantity)));
  };

  const getAddonIcon = () => {
    switch (addon.type) {
      case 'illustrations':
        return <Coins className="h-5 w-5 text-purple-600" />;
      case 'storage':
        return addon.amountInt === null ? 
          <Infinity className="h-5 w-5 text-teal-600" /> : 
          <HardDrive className="h-5 w-5 text-teal-600" />;
      default:
        return <ShoppingCart className="h-5 w-5" />;
    }
  };

  const getAddonDescription = () => {
    switch (addon.type) {
      case 'illustrations':
        return 'Generate beautiful AI illustrations for your stories';
      case 'storage':
        return addon.amountInt === null ? 
          'Store unlimited stories, audio, and media forever' :
          'Additional storage space for your stories and media';
      default:
        return 'Add-on item';
    }
  };

  const getPricePerUnit = () => {
    if (addon.amountInt === null) return addon.price;
    return addon.price / addon.amountInt;
  };

  const totalPrice = addon.price * quantity;

  return (
    <Card 
      className={`transition-all duration-200 hover:shadow-lg hover:border-purple-200 dark:hover:border-purple-800 ${className}`}
      data-testid={testId}
    >
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getAddonIcon()}
            <div>
              <div className="text-lg font-semibold" data-testid={`${testId}-title`}>
                {formatAddonAmount(addon)}
              </div>
              <div className="text-sm text-muted-foreground font-normal">
                {addon.type === 'illustrations' ? 'Illustration Credits' : 'Storage Space'}
              </div>
            </div>
          </div>
          {addon.type === 'storage' && addon.amountInt === null && (
            <Badge className="bg-gradient-to-r from-purple-600 to-teal-600 text-white">
              Popular
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground" data-testid={`${testId}-description`}>
          {getAddonDescription()}
        </p>

        {/* Pricing Info */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Unit Price:</span>
            <span className="font-medium" data-testid={`${testId}-unit-price`}>
              ${addon.price.toFixed(2)}
            </span>
          </div>
          {addon.amountInt !== null && addon.amountInt > 1 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Per {addon.type === 'illustrations' ? 'credit' : 'GB'}:
              </span>
              <span className="text-sm" data-testid={`${testId}-per-unit-price`}>
                ${getPricePerUnit().toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* Quantity Selector */}
        <div className="space-y-2">
          <Label htmlFor={`quantity-${addon.id}`} className="text-sm font-medium">
            Quantity
          </Label>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuantityChange(quantity - 1)}
              disabled={quantity <= 1}
              data-testid={`${testId}-quantity-decrease`}
            >
              <Minus className="h-3 w-3" />
            </Button>
            <Input
              id={`quantity-${addon.id}`}
              type="number"
              value={quantity}
              onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
              className="text-center w-20"
              min="1"
              max="10"
              data-testid={`${testId}-quantity-input`}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuantityChange(quantity + 1)}
              disabled={quantity >= 10}
              data-testid={`${testId}-quantity-increase`}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Total Price */}
        <div className="bg-muted rounded-lg p-3">
          <div className="flex justify-between items-center">
            <span className="font-medium">Total:</span>
            <span 
              className="text-xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent"
              data-testid={`${testId}-total-price`}
            >
              ${totalPrice.toFixed(2)}
            </span>
          </div>
          {quantity > 1 && (
            <div className="text-sm text-muted-foreground mt-1">
              {quantity} × {formatAddonAmount(addon)}
            </div>
          )}
        </div>

        {/* Purchase Button */}
        <Button
          onClick={handlePurchase}
          disabled={purchaseAddon.isPending || !addon.isActive}
          className="w-full bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
          size="lg"
          data-testid={`${testId}-purchase-button`}
        >
          {purchaseAddon.isPending ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              Processing...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Buy Now - ${totalPrice.toFixed(2)}
            </div>
          )}
        </Button>

        {!addon.isActive && (
          <p className="text-sm text-muted-foreground text-center">
            This add-on is currently unavailable
          </p>
        )}
      </CardContent>
    </Card>
  );
}